import { ArrowUpRight, Clock3, FileText, LockKeyhole, Mail, MapPin, Phone, ShieldCheck, Sparkles, Truck } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useTranslation } from '@sshop/i18n';
import { Container } from '@sshop/ui';

import { SocialLinks, useContactList } from '@/features/contact';
import { useLocale } from '@/shared/hooks/useLocale';
import { routes } from '@/shared/lib/routes';

import { Brand } from './Header/Brand/Brand';

const titleClass = 'text-fg-primary mb-4 text-sm font-semibold';
const linkClass =
  'group text-fg-secondary hover:text-accent inline-flex items-center gap-1.5 transition-colors duration-fast ease-token';
const trustLinks = [
  { key: 'shipping', Icon: Truck, to: routes.shippingPayment },
  { key: 'privacy', Icon: LockKeyhole, to: routes.privacyPolicy },
  { key: 'guarantee', Icon: ShieldCheck, to: routes.guarantee },
  { key: 'terms', Icon: FileText, to: routes.termsConditions },
] as const;

export function Footer() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { pathname } = useLocation();
  const currentPath = pathname.replace(/\/+$/, '');
  const followsNewsSection = currentPath === `/${locale}` || currentPath === `/${locale}/news`;
  const { data: contacts } = useContactList();
  const contact = contacts?.[0];
  const mapQuery = [contact?.address, contact?.city, contact?.country].filter(Boolean).join(', ');

  return (
    <footer className={`border-border-subtle border-t ${followsNewsSection ? 'bg-bg-subtle' : 'bg-bg-subtle/70 mt-12'}`}>
      <Container size="xl" className="py-8 sm:py-10 lg:py-14">
        <div className="border-border-subtle bg-bg-canvas grid overflow-hidden rounded-2xl border shadow-sm lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1.85fr)] lg:rounded-3xl">
          <section className="border-border-subtle relative overflow-hidden border-b p-6 sm:p-8 lg:border-r lg:border-b-0 lg:p-10">
            <div
              className="bg-accent/10 pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full blur-3xl"
              aria-hidden
            />
            <Link
              to={routes.home(locale)}
              className="focus-visible:ring-accent relative inline-flex rounded-lg outline-none focus-visible:ring-2"
              aria-label={t('app.homeAria')}
            >
              <Brand />
            </Link>
            <p className="text-fg-secondary relative mt-5 max-w-sm text-sm leading-6 sm:text-base">
              {t('footer.tagline')}
            </p>
            <Link
              to={routes.special(locale)}
              className="bg-accent text-accent-fg duration-base ease-token relative mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md"
            >
              <Sparkles className="h-4 w-4" aria-hidden />
              <span>{t('footer.shopDeals')}</span>
              <ArrowUpRight className="h-4 w-4" aria-hidden />
            </Link>

            <div className="relative mt-8 grid grid-cols-1 gap-2 min-[420px]:grid-cols-2 sm:max-w-md">
              {trustLinks.map(({ key, Icon, to }) => (
                <Link key={key} to={to(locale)} className="border-border-subtle bg-bg-subtle/70 hover:border-accent focus-visible:ring-accent flex min-h-14 min-w-0 items-center gap-2.5 rounded-xl border p-3 transition-colors focus-visible:outline-none focus-visible:ring-2">
                  <Icon className="text-accent h-5 w-5 shrink-0" aria-hidden />
                  <span className="text-fg-secondary min-w-0 flex-1 text-xs font-medium leading-snug [overflow-wrap:anywhere]">{t(`home.trust.${key}.title`)}</span>
                </Link>
              ))}
            </div>
          </section>

          <div className="grid gap-8 p-6 sm:grid-cols-2 sm:p-8 lg:grid-cols-3 lg:p-10">
            <nav aria-label={t('footer.browse')}>
              <h2 className={titleClass}>{t('footer.browse')}</h2>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link to={routes.home(locale)} className={linkClass}>
                    {t('nav.home')}
                  </Link>
                </li>
                <li>
                  <Link to={routes.catalog(locale)} className={linkClass}>
                    {t('nav.catalog')}
                  </Link>
                </li>
                <li>
                  <Link to={routes.cart(locale)} className={linkClass}>
                    {t('nav.cart')}
                  </Link>
                </li>
                <li>
                  <Link to={routes.promotions(locale)} className={linkClass}>
                    {t('nav.promotions')}
                  </Link>
                </li>
                <li>
                  <Link to={routes.favorites(locale)} className={linkClass}>
                    {t('nav.favorites')}
                  </Link>
                </li>
              </ul>
            </nav>

            <nav aria-label={t('footer.info')}>
              <h2 className={titleClass}>{t('footer.info')}</h2>
              <ul className="space-y-3 text-sm">
                <li>
                  <Link to={routes.about(locale)} className={linkClass}>
                    {t('nav.about')}
                  </Link>
                </li>
                <li>
                  <Link to={routes.news(locale)} className={linkClass}>
                    {t('nav.news')}
                  </Link>
                </li>
                <li>
                  <Link to={routes.contact(locale)} className={linkClass}>
                    {t('nav.contact')}
                  </Link>
                </li>
              </ul>
            </nav>

            <section className="sm:col-span-2 lg:col-span-1" aria-label={t('footer.contact')}>
              <h2 className={titleClass}>{t('footer.contact')}</h2>
              <div className="border-border-subtle bg-bg-subtle/55 space-y-3 rounded-xl border p-4 text-sm">
                {contact?.phone ? (
                  <a href={`tel:${contact.phone}`} className={`${linkClass} w-full`}>
                    <Phone className="text-accent h-4 w-4 shrink-0" aria-hidden />
                    <span className="break-all">{contact.phone}</span>
                  </a>
                ) : null}
                {contact?.email ? (
                  <a href={`mailto:${contact.email}`} className={`${linkClass} w-full`}>
                    <Mail className="text-accent h-4 w-4 shrink-0" aria-hidden />
                    <span className="break-all">{contact.email}</span>
                  </a>
                ) : null}
                {mapQuery ? (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(mapQuery)}`}
                    target="_blank"
                    rel="noreferrer"
                    className={`${linkClass} items-start`}
                  >
                    <MapPin className="text-accent mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{mapQuery}</span>
                  </a>
                ) : null}
                {contact?.workingHours ? (
                  <p className="text-fg-secondary flex items-start gap-2">
                    <Clock3 className="text-accent mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>{contact.workingHours}</span>
                  </p>
                ) : null}
                {!contact ? <p className="text-fg-muted">{t('footer.contactHint')}</p> : null}
              </div>
              {contact ? (
                <SocialLinks className="mt-4" facebook={contact.facebook} instagram={contact.instagram} />
              ) : null}
            </section>
          </div>
        </div>
      </Container>

      <div className="border-border-subtle border-t">
        <Container
          size="xl"
          className="text-fg-secondary min-h-14 flex flex-col items-center justify-between gap-1 py-3 text-center text-xs sm:flex-row sm:text-left"
        >
          <span>
            &copy; {new Date().getFullYear()} SSHOP. {t('footer.rights')}
          </span>
        </Container>
      </div>
    </footer>
  );
}
